import { useEffect, useState } from 'react';
import { Banner, Button } from '@/shared/ui/primitives';
import { Sparkles } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { TEST_IDS } from '@/testIds';
import { createSupportToastAction } from '@/shared/lib/toastEvents';

import { subscriptionQueryOptions } from '../api';
import { isReadOnlyMode } from '../model';

import { PAYMENT_PHASE_CTA } from './paymentPhaseCopy';
import styles from './SubscriptionBanner.module.css';
import { useSubscriptionPayment } from './SubscriptionPaymentContext';
import type { SubscriptionPaymentPhase } from './useSubscribeWithStars';

const SUBSCRIPTION_BANNER_TITLE = 'Subscription required';
const SUBSCRIPTION_BANNER_MESSAGE = 'Subscribe to restore access to all features';
const MAX_SUBSCRIPTION_REFRESH_DELAY_MS = 24 * 60 * 60 * 1000;
const PAST_ENDS_AT_REFRESH_DELAY_MS = 60 * 1000;

const PAYMENT_PHASE_MESSAGE: Record<SubscriptionPaymentPhase, string> = {
  idle: SUBSCRIPTION_BANNER_MESSAGE,
  creatingCheckout: 'Starting payment...',
  waitingInvoice: 'Preparing your Telegram Stars invoice...',
  openingInvoice: 'Complete the payment in Telegram',
  confirmingSubscription: 'Activating your subscription...',
  awaitingConfirmation: 'Payment is processing. Access will update automatically.',
  confirmationNeedsSupport:
    'Payment could not be confirmed automatically. Contact support before trying again.',
};

export function SubscriptionBanner() {
  const { data: activeSubscription, refetch } = useQuery(subscriptionQueryOptions.active());
  const { subscribe, phase, isPending, isAwaitingConfirmation, needsSupport } =
    useSubscriptionPayment();
  const [refreshCycle, setRefreshCycle] = useState(0);

  useEffect(() => {
    if (!activeSubscription) return;

    const endsAt = Date.parse(activeSubscription.endsAt);
    if (!Number.isFinite(endsAt)) return;

    const remainingMs = endsAt - Date.now();
    const refreshDelayMs =
      remainingMs > 0
        ? Math.min(remainingMs + 250, MAX_SUBSCRIPTION_REFRESH_DELAY_MS)
        : PAST_ENDS_AT_REFRESH_DELAY_MS;
    const timeoutId = window.setTimeout(() => {
      setRefreshCycle((cycle) => cycle + 1);
      void refetch();
    }, refreshDelayMs);

    return () => window.clearTimeout(timeoutId);
  }, [activeSubscription, refreshCycle, refetch]);

  if (!isReadOnlyMode(activeSubscription)) return null;

  const handleSubscribe = () => {
    if (needsSupport) {
      createSupportToastAction().onClick();
      return;
    }
    void subscribe().catch(() => undefined);
  };

  return (
    <div className={styles.root} data-testid={TEST_IDS.subscriptionBanner}>
      <Banner
        icon={
          <span className={styles.icon} aria-hidden="true">
            <Sparkles size={18} />
          </span>
        }
        title={SUBSCRIPTION_BANNER_TITLE}
        description={PAYMENT_PHASE_MESSAGE[phase]}
        aria-live="polite"
      >
        <Button
          size="md"
          variant="primary"
          fullWidth
          loading={isPending}
          disabled={isPending || isAwaitingConfirmation}
          onClick={handleSubscribe}
          data-testid={TEST_IDS.subscriptionSubscribeButton}
        >
          {PAYMENT_PHASE_CTA[phase]}
        </Button>
      </Banner>
    </div>
  );
}
